import { get } from 'lodash';
import { Upload } from 'sugar-design';

import { errorToast } from './toasts';
import * as types from '@/redux/actionTypes';
import { ERROR_CODES } from '@/tools/mokaRequest/errorCode';
import mokaRequest from '@/tools/mokaRequest';
import { isEmpty } from '@/utils/common';
import { getI18n } from '@/utils/i18n';

const i18n = getI18n();

function loadingForUploadingResume({ percent, loading = true }) {
  return {
    type: types.UPLOAD_RESUME,
    payload: {
      loading,
      percent: Number(percent),
    },
  };
}

function loadingIndicator(loading = true) {
  return {
    type: types.LOAD_APPLICATION_LIST,
    payload: loading,
  };
}

function parseResume(data = {}) {
  return {
    type: types.UPLOAD_RESUME,
    payload: data,
  };
}

export function fetchApplication(orgId, applicationId) {
  return (dispatch) =>
    mokaRequest('/api/outer/ats-candidate/candidate/headhunters/application', {
      query: { orgId, applicationId },
      interceptOnError: true,
      actionName: i18n.t`获取候选人`,
    })
      .get()
      .then((application) => {
        dispatch({
          type: types.UPDATE_APPLICATION,
          payload: { application },
        });
      });
}

export function fetchExcelApplicationStats(orgId) {
  return (dispatch) => {
    dispatch(loadingIndicator());
    mokaRequest('/api/outer/ats-candidate/candidate/headhunters/applicationExcelStats', {
      query: { orgId },
      interceptOnError: true,
      actionName: i18n.t`获取Excel推荐记录`,
    })
      .get()
      .then((data) => {
        dispatch(loadingIndicator(false));
        dispatch({
          type: types.UPDATE_EXCEL_APPLICATION_LIST,
          payload: data,
        });
      });
  };
}

export function uploadResume({ file, orgId }) {
  return (dispatch) => {
    mokaRequest('/api/outer/ats-candidate-extractor/resume/uploadAndParse', {
      interceptOnError: true,
      interceptOnSuccess: true,
      actionName: i18n.t`上传简历`,
    })
      .headers([{ key: 'ws-id', value: window._wsConnection?.id || '' }])
      .attach({
        data: {
          orgId,
          fromHeadhunter: true,
        },
        files: [
          {
            field: 'resume',
            file: file,
            option: file.name,
          },
        ],
        onProcess: (e) => {
          const { percent } = e;
          dispatch(loadingForUploadingResume({ percent }));
        },
      })
      .then((result) => {
        dispatch(loadingForUploadingResume({ loading: false }));
        dispatch(parseResume(result));
      })
      .catch(() => {
        dispatch(loadingForUploadingResume({ loading: false }));
      });
  };
}

export function cleanTempResume() {
  return (dispatch) => dispatch(parseResume({}));
}

export function uploadCandidate({
  jobId,
  orgId,
  application,
  isResumeUploaded,
  rpo,
  contractId,
  isConfirmResumeAuthorization,
  callback = () => {},
}) {
  return (dispatch) => {
    const url = application.id
      ? '/api/outer/ats-candidate/candidate/headhunters/updateApplication'
      : '/api/outer/ats-jc/application/headhunter/create';
    mokaRequest(url)
      .post({
        application,
        orgId,
        jobId,
        isResumeUploaded,
        rpo,
        contractId,
        isConfirmResumeAuthorization,
      })
      .then((data) => {
        // 获取查重信息
        dispatch({
          type: types.UPLOAD_CANDIDATE_RESULT,
          payload: { result: 'success', data },
        });
        if (!application.id && !data.merged) {
          dispatch({
            type: types.ADD_APPLICATION_NUM,
            payload: { orgId, numAdded: 1 },
          });
        }
        callback();
      })
      .catch((error) => {
        if (
          [
            ERROR_CODES[700024],
            ERROR_CODES[701012],
            ERROR_CODES[701026],
            ERROR_CODES[701027],
          ].includes(error?.response?.body?.code)
        ) {
          const data = isEmpty(error.response.body.data)
            ? error.response.body
            : error.response.body.data;
          dispatch({
            type: types.UPLOAD_CANDIDATE_RESULT,
            payload: { result: 'error', data: { ...data, code: error.response.body.code } },
          });
          callback(error.response.body.code);
        } else {
          dispatch(errorToast(error.response?.body?.msg || i18n.t`服务器错误`));
          callback(error);
        }
      });
  };
}

export function uploadCandidateForHeadhunter(param, callback) {
  return (dispatch) => {
    mokaRequest('/api/outer/ats-candidate/candidate/headhunters/updateApplication/v2')
      .post(param)
      .then((data) => {
        dispatch({
          type: types.UPLOAD_CANDIDATE_RESULT,
          payload: { result: 'success', data },
        });
        callback();
      })
      .catch((error) => {
        if ([ERROR_CODES[400222]].includes(error.response.body.code)) {
          const data = isEmpty(error.response.body.data)
            ? error.response.body
            : error.response.body.data;
          dispatch({
            type: types.UPLOAD_CANDIDATE_RESULT,
            payload: { result: 'error', data: { ...data, code: error.response.body.code } },
          });
          callback(error.response.body.code);
        } else {
          dispatch(errorToast(error.response?.body?.msg || i18n.t`服务器错误`));
          callback(error);
        }
      });
  };
}

// eslint-disable-next-line max-params
export function checkDuplication({
  orgId,
  jobId,
  application,
  contractId,
  isRpo,
  callback = () => {},
}) {
  return () => {
    mokaRequest('/api/outer/ats-jc/headhunter/headhunters/checkDup', {
      interceptOnError: true,
      actionName: i18n.t`检查重复`,
    })
      .post({ orgId, jobId, application, contractId, isRpo })
      .then((data = {}) => {
        callback(null, data);
      })
      .catch((error) => callback(error));
  };
}

export function getAiCheckDupId({ orgId, checkApplication, isRpo, jobId, callback = () => {} }) {
  return () => {
    mokaRequest('/api/outer/ats-jc/headhunter/headhunters/getAiCheckDupId', {
      interceptOnError: true,
      actionName: i18n.t`进行ai查重`,
    })
      .post({ orgId, checkApplication, isRpo, jobId })
      .then((data = {}) => {
        callback(null, data);
      })
      .catch((error) => callback(error));
  };
}

// 轮训ai查重结果
export function aiCheckSimilarResult({ orgId, queryAiResultId, callback = () => {} }) {
  return () => {
    mokaRequest('/api/outer/ats-jc/headhunter/headhunters/aiCheckSimilarResult', {
      interceptOnError: true,
      actionName: i18n.t`ai查重`,
    })
      .post({ orgId, queryAiResultId })
      .then((data = {}) => {
        callback(null, data);
      })
      .catch((error) => callback(error));
  };
}

/**
 * 批量推荐候选人
 * @param {String} orgId
 * @param {Object} data
 *   @prop {Array} candidates
 *   @prop {String} jobId
 *   @prop {String} excelKey
 *   @prop {String} excelName
 * @param {Func} callback
 */
export function batchUploadCandidates(orgId, data, callback = () => {}) {
  return (dispatch) => {
    mokaRequest('/api/outer/ats-jc/application/headhunter/batchCreate')
      .post({ ...data, orgId })
      .then((resData) => {
        const {
          applicationsDuped,
          applicationsProtected,
          enterDup = false,
          successCount = 0,
          failedCount = 0,
        } = resData;
        // 被merge的人和被保护期拒绝的人都不算有效投递，不计数
        let numAdded = get(data, 'candidates.length');
        if (applicationsDuped) {
          let numMerged = 0;
          applicationsDuped.forEach((app) => {
            if (app.merged) {
              numMerged += 1;
            }
          });
          numAdded -= numMerged;
          // 此次上传中重复的候选人
          dispatch({
            type: types.REPLACE_EXCEL_DUP_APPLICATIONS,
            payload: { applications: applicationsDuped },
          });
        }
        if (applicationsProtected) {
          numAdded -= applicationsProtected.length;
        }
        // 更新总推荐人数
        dispatch({ type: types.ADD_APPLICATION_NUM, payload: { orgId, numAdded } });
        // 更新是否进入查重
        dispatch({
          type: types.ENTERING_DUPLICATE,
          payload: { enterDup, successCount, failedCount },
        });
        callback();
      })
      .catch((error) => {
        callback(error.message);
      });
  };
}

export function clearExcelDupApplications() {
  return { type: types.CLEAR_EXCEL_DUP_APPLICATIONS };
}

export function getDupApplicationsByImportId(orgId, importId, callback) {
  return (dispatch) => {
    mokaRequest('/api/outer/ats-candidate/candidate/headhunters/getDupApplications', {
      query: {
        orgId,
        id: importId,
      },
    })
      .get()
      .then((data) => {
        dispatch({
          type: types.REPLACE_EXCEL_DUP_APPLICATIONS,
          payload: { applications: data },
        });
        callback();
      })
      .catch((error) => {
        callback(error);
      });
  };
}

export function clearApplicationInfo() {
  return { type: types.CLEAR_APPLICATION_INFO };
}

/**
 * 上传文件
 * @param {File} file
 * @param {Function} onProgress
 * @param {Function} callback
 */
export function uploadFile({ file, orgId, onProgress }, callback = () => {}) {
  mokaRequest('/api/outer/ats-common/file/upload')
    .attach({
      files: [{ field: 'file', file, option: file.name }],
      data: { orgId, uploadType: 'private' },
      onProcess: (e) => {
        if (typeof onProgress === 'function') {
          onProgress({
            ...file,
            uid: file.uid,
            name: file.name,
            type: file.type,
            size: file.size,
            lastModified: file.lastModified,
            percent: e.percent,
            status: Upload.fileStatus.uploading,
          });
        }
      },
    })
    .then(({ key, url }) => callback(null, { key, url }))
    .catch((error) => callback(error));
}

/**
 * 查询已推荐候选人记录，推荐状态分组数量
 * @param {string} orgId
 * @returns {Object} data
 *   @prop {number} recommending
 *   @prop {number} recommendSuccess
 *   @prop {number} recommendFailed
 */

export function fetchApplicationsCountOfRecommendStatus(orgId) {
  return mokaRequest(
    '/api/outer/ats-jc/application/headhunter/headhunters/applications/count'
  ).post({ orgId });
}
